import { translateError, translateIssue } from '@gx-cms/error-messages'

import { useAuthStore } from '../../features/auth/hooks'
import { currentTranslation } from '../i18n/current'

import type { Session } from './api.types'
import type {
  ErrorEnvelopeBody,
  FieldIssue,
  RequestOptions,
} from './client.types'

/** Error envelope from the API (docs/04 §5). */
export class ApiError extends Error {
  /** Translation key and parameters of the message (ADR-045). */
  messageKey?: string
  params?: Record<string, string | number>
  /** The server's English text. */
  englishMessage?: string

  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details: FieldIssue[] = [],
    readonly correlationId?: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }

  /**
   * Builds the error from the server's envelope with the message and field
   * issues in the admin language; English when there is no translation.
   */
  static fromEnvelope(
    status: number,
    env: ErrorEnvelopeBody | undefined,
    fallback: string,
    correlationId?: string,
  ): ApiError {
    const { locale } = currentTranslation()

    const err = new ApiError(
      status,
      env?.code ?? 'ERROR',
      env ? translateError(locale, env) : fallback,
      (env?.details ?? []).map((d) => ({
        ...d,
        message: translateIssue(locale, d),
      })),
      env?.correlationId ?? correlationId,
    )

    err.messageKey = env?.messageKey
    err.params = env?.params
    err.englishMessage = env?.message

    return err
  }
}

export const API_BASE = '/api/v1'

function readCookie(name: string): string | undefined {
  return document.cookie
    .split('; ')
    .find((c) => c.startsWith(`${name}=`))
    ?.slice(name.length + 1)
}

let refreshing: Promise<Session | null> | null = null

/**
 * Exchanges the HttpOnly refresh cookie for a new access token. Single-flight:
 * concurrent 401s share one refresh (refresh tokens rotate on every use).
 */
export function refreshSession(): Promise<Session | null> {
  refreshing ??= (async () => {
    try {
      const csrf = readCookie('cms_csrf')

      if (!csrf) return null
      const res = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'x-csrf-token': decodeURIComponent(csrf) },
      })

      if (!res.ok) return null
      const session = (await res.json()) as Session

      useAuthStore.getState().setSession(session)

      return session
    } catch {
      return null
    } finally {
      setTimeout(() => {
        refreshing = null
      }, 0)
    }
  })()

  return refreshing
}

export async function api<T>(
  path: string,
  opts: RequestOptions = {},
  retried = false,
): Promise<T> {
  const url = new URL(`${API_BASE}${path}`, window.location.origin)

  for (const [k, v] of Object.entries(opts.query ?? {})) {
    if (v !== undefined && v !== null && v !== '')
      url.searchParams.set(k, String(v))
  }

  const headers: Record<string, string> = { accept: 'application/json' }
  const token = useAuthStore.getState().accessToken

  if (token) headers.authorization = `Bearer ${token}`
  let body: BodyInit | undefined = opts.rawBody

  if (opts.body !== undefined) {
    headers['content-type'] = 'application/json'
    body = JSON.stringify(opts.body)
  }

  let res: Response

  try {
    res = await fetch(url, {
      method: opts.method ?? 'GET',
      headers,
      body,
      credentials: 'same-origin',
      signal: opts.signal,
    })
  } catch (err) {
    if ((err as Error).name === 'AbortError') throw err
    throw new ApiError(0, 'NETWORK', currentTranslation().t.errors.offline)
  }

  if (res.status === 401 && !retried && !path.startsWith('/auth/')) {
    const session = await refreshSession()

    if (session) return api<T>(path, opts, true)
    useAuthStore.getState().clear()
  }

  if (res.status === 204) return undefined as T
  const text = await res.text()
  const data = text ? safeJson(text) : undefined

  if (!res.ok) {
    const env = (data as { error?: ErrorEnvelopeBody } | undefined)?.error

    throw ApiError.fromEnvelope(
      res.status,
      env,
      `${currentTranslation().t.errors.generic} (${res.status})`,
      res.headers.get('x-correlation-id') ?? undefined,
    )
  }

  return data as T
}

/** Upload with progress (fetch has no upload progress events). */
export function upload<T>(
  path: string,
  form: FormData,
  onProgress?: (fraction: number) => void,
): Promise<T> {
  const send = (retried: boolean): Promise<T> =>
    new Promise<T>((resolve, reject) => {
      const xhr = new XMLHttpRequest()

      xhr.open('POST', `${API_BASE}${path}`)
      const token = useAuthStore.getState().accessToken

      if (token) xhr.setRequestHeader('authorization', `Bearer ${token}`)
      xhr.upload.onprogress = (e) =>
        e.lengthComputable && onProgress?.(e.loaded / e.total)

      xhr.onload = async () => {
        if (xhr.status === 401 && !retried && (await refreshSession()))
          return resolve(send(true))
        const data = xhr.responseText ? safeJson(xhr.responseText) : undefined

        if (xhr.status >= 200 && xhr.status < 300) return resolve(data as T)
        const env = (data as { error?: ErrorEnvelopeBody } | undefined)?.error

        reject(
          ApiError.fromEnvelope(
            xhr.status,
            env,
            currentTranslation().t.errors.uploadFailed,
          ),
        )
      }

      xhr.onerror = () =>
        reject(
          new ApiError(
            0,
            'NETWORK',
            currentTranslation().t.errors.uploadNetwork,
          ),
        )
      xhr.send(form)
    })

  return send(false)
}

/**
 * Fetches a file with the user's session and saves it in the admin window
 * (plugin frames cannot download with credentials themselves, SDK 1.5).
 */
export async function download(
  path: string,
  opts: RequestOptions & { fileName?: string } = {},
  retried = false,
): Promise<{ fileName: string; size: number }> {
  const url = new URL(`${API_BASE}${path}`, window.location.origin)

  for (const [k, v] of Object.entries(opts.query ?? {})) {
    if (v !== undefined && v !== null && v !== '')
      url.searchParams.set(k, String(v))
  }

  const headers: Record<string, string> = {}
  const token = useAuthStore.getState().accessToken

  if (token) headers.authorization = `Bearer ${token}`
  if (opts.body !== undefined) headers['content-type'] = 'application/json'

  const res = await fetch(url, {
    method: opts.method ?? 'GET',
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    credentials: 'same-origin',
  }).catch(() => {
    throw new ApiError(0, 'NETWORK', currentTranslation().t.errors.offline)
  })

  if (res.status === 401 && !retried && (await refreshSession()))
    return download(path, opts, true)

  if (!res.ok) {
    const data = safeJson(await res.text())
    const env = (data as { error?: ErrorEnvelopeBody } | undefined)?.error

    throw ApiError.fromEnvelope(
      res.status,
      env,
      `${currentTranslation().t.errors.generic} (${res.status})`,
    )
  }

  const disposition = res.headers.get('content-disposition') ?? ''

  const fromHeader =
    /filename\*=UTF-8''([^;]+)/i.exec(disposition)?.[1] ??
    /filename="?([^";]+)"?/i.exec(disposition)?.[1]

  const fileName =
    opts.fileName ?? (fromHeader ? decodeURIComponent(fromHeader) : 'download')

  const blob = await res.blob()
  const href = URL.createObjectURL(blob)
  const a = document.createElement('a')

  a.href = href
  a.download = fileName
  document.body.append(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(href), 10_000)

  return { fileName, size: blob.size }
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}

export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message
  if (err instanceof Error) return err.message

  return currentTranslation().t.errors.generic
}

/** Field issues keyed by path for inline form errors. */
export function fieldErrors(err: unknown, prefix = ''): Record<string, string> {
  if (!(err instanceof ApiError)) return {}
  const out: Record<string, string> = {}

  for (const d of err.details) {
    const key =
      prefix && d.path.startsWith(`${prefix}.`)
        ? d.path.slice(prefix.length + 1)
        : d.path

    out[key] ??= d.message
  }

  return out
}
